import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useTarefasByData, useTarefasByPeriodo, useFuncionarios, useAtividades } from "@/hooks/use-equipe";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { format, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { MapPin, ClipboardList } from "lucide-react";

import { PageHeader } from "@/components/layout/page-header";
import { avatarCor } from "@/lib/avatar-cor";

interface TarefaGroup { data: string; atividadeId: string; setores: string[]; obs: string | null; }

function groupFuturas(tarefas: any[]): TarefaGroup[] {
  const map = new Map<string, TarefaGroup>();
  for (const t of tarefas) {
    const key = `${t.data}|${t.atividadeId}`;
    if (!map.has(key)) map.set(key, { data: t.data, atividadeId: t.atividadeId, setores: [], obs: t.observacoes ?? null });
    if (t.setorId) map.get(key)!.setores.push(t.setorId);
  }
  return Array.from(map.values()).sort((a, b) => a.data.localeCompare(b.data));
}

export default function EquipeTv() {
  const [, setLocation] = useLocation();
  const [agora, setAgora] = useState(new Date());
  const { user } = useAuth();

  const hoje = format(new Date(), "yyyy-MM-dd");
  const amanha = format(addDays(new Date(), 1), "yyyy-MM-dd");
  const em7dias = format(addDays(new Date(), 7), "yyyy-MM-dd");

  const { data: tarefasHoje = [] } = useTarefasByData(hoje);
  const { data: tarefasFuturas = [] } = useTarefasByPeriodo(amanha, em7dias);
  const { data: funcionarios = [] } = useFuncionarios();
  const { data: atividades = [] } = useAtividades();
  const { data: setores = [] } = useQuery<{ id: string; nome: string }[]>({ queryKey: ["/api/setores"] });

  const atividadeMap = new Map(atividades.map(a => [a.id, a]));
  const setorMap = new Map(setores.map(s => [s.id, s.nome]));

  useEffect(() => {
    const timer = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => window.location.reload(), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#1a2e0d] text-white p-6 relative select-none">
      {/* Exceção do sistema: o corpo desta tela mantém o tipo grande (leitura à
          distância). Só o cabeçalho entra na casca comum. O subtítulo não usa a
          prop `descricao` porque `text-muted-foreground` é escuro demais sobre o
          verde do fundo — aqui ele continua em branco translúcido.

          O título é "Plano do dia", não "Modo TV": numa tela de parede o título
          responde "o que estou vendo", não "que item de menu eu cliquei". A
          entrada da sidebar continua sendo "Modo TV". */}
      <div className="flex items-start justify-between mb-6">
        <div className="min-w-0">
          <PageHeader titulo="Plano do dia" />
          <p className="text-white/50 text-sm mt-0.5">
            🌿 {user?.orgName ?? "Fazenda"} · atualizado agora
          </p>
        </div>
        <div className="text-right flex flex-col items-end gap-1">
          <button
            onClick={() => setLocation("/equipe")}
            className="text-white/40 hover:text-white text-xs flex items-center gap-1 transition-colors"
          >
            ✕ Sair do modo TV
          </button>
          <p className="text-white/70 text-sm capitalize">
            {format(agora, "EEEE, d 'de' MMMM", { locale: ptBR })}
          </p>
          <p className="text-4xl font-bold tabular-nums tracking-tight leading-none">
            {format(agora, "HH:mm")}
          </p>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
        {funcionarios.map(func => {
          const tarefasFunc = tarefasHoje
            .filter(t => t.funcionarioId === func.id && t.status !== "CANCELADA");

          const emAndamento = tarefasFunc.find(t => t.status === "EM_ANDAMENTO");
          const proxima = tarefasFunc.filter(t => t.status === "PENDENTE")[0] ?? null;
          const tarefaPrincipal = emAndamento ?? proxima ?? null;
          const statusLabel = emAndamento ? "EM ANDAMENTO" : proxima ? "PRÓXIMA TAREFA" : "SEM TAREFA HOJE";
          const temTarefa = !!tarefaPrincipal;

          // Setores da tarefa principal (pode ter vários se agrupados)
          const setoresPrincipal = tarefasFunc
            .filter(t => t.atividadeId === tarefaPrincipal?.atividadeId && t.status === tarefaPrincipal?.status)
            .map(t => t.setorId ? setorMap.get(t.setorId) : null)
            .filter(Boolean) as string[];

          const ativPrincipal = tarefaPrincipal ? atividadeMap.get(tarefaPrincipal.atividadeId ?? "") : null;

          // Próximos dias
          const futurasBruto = tarefasFuturas.filter(t => t.funcionarioId === func.id && t.status !== "CANCELADA");
          const futuras = groupFuturas(futurasBruto).slice(0, 3);

          const inicial = (func.apelido ?? func.nome).charAt(0).toUpperCase();
          const color = avatarCor(func.id);

          return (
            <div key={func.id} className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3 flex flex-col">
              {/* Avatar + Nome */}
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-base font-bold shrink-0"
                  style={{ backgroundColor: color }}
                >
                  {inicial}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-base leading-tight truncate">{func.apelido ?? func.nome}</p>
                  <p className="text-white/40 text-xs uppercase tracking-wide truncate">
                    {func.cargo ?? ativPrincipal?.nome ?? "—"}
                  </p>
                </div>
              </div>

              {/* Tarefa principal */}
              <div className={`rounded-lg p-3 space-y-1.5 ${temTarefa ? "bg-[#7a6520]/50 border border-atencao/30" : "bg-white/5"}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-atencao-claro/90">
                  {statusLabel}
                </p>
                {temTarefa ? (
                  <>
                    <p className="font-bold text-lg leading-snug">{ativPrincipal?.nome ?? "Tarefa"}</p>
                    {setoresPrincipal.length > 0 && (
                      <p className="text-sm text-white/70 flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        {setoresPrincipal.join(", ")}
                      </p>
                    )}
                    {tarefaPrincipal?.observacoes && (
                      <p className="text-xs text-white/50 flex items-center gap-1">
                        <ClipboardList className="h-3.5 w-3.5 shrink-0" />
                        {tarefaPrincipal.observacoes}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-white/25 text-3xl font-bold leading-none">—</p>
                )}
              </div>

              {/* Próximos dias */}
              {futuras.length > 0 && (
                <div className="flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/30 mb-1.5">
                    Próximos dias
                  </p>
                  <div className="space-y-1">
                    {futuras.map((g, i) => {
                      const ativ = atividadeMap.get(g.atividadeId);
                      const setNomes = g.setores.map(id => setorMap.get(id)).filter(Boolean).join(", ");
                      return (
                        <p key={i} className="text-xs text-white/55 leading-snug">
                          <span className="text-white/35 capitalize">
                            {format(new Date(g.data + "T00:00:00"), "EEE dd/MM", { locale: ptBR })}:{" "}
                          </span>
                          {ativ?.nome ?? "Tarefa"}
                          {setNomes ? ` · ${setNomes}` : ""}
                        </p>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
